import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslate } from '../hooks/useTranslate';
import { VscClose, VscFeedback, VscTag, VscThumbsup, VscThumbsdown } from 'react-icons/vsc';
import { feedbackService, FeedbackFormData } from '../services/feedbackService';
import { HistoricalReportResponse } from '../types/historical';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../contexts/AuthContext';
import '../styles/StarRating.css';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
    dataSources?: Array<{
        id: number;
        uuid?: string; // Make optional to match DataSource type
        name: string;
        status: string;
        createdAt?: string;
    }>;
    historicalReports?: HistoricalReportResponse[];
    currentReportId?: string; // Report ID from URL when opened from report page
    currentReportName?: string; // Report name passed from report page (avoids duplicate API call)
}

const RATING_OPTIONS = [
    { value: 1, label: 'Very Poor', icon: '⭐☆☆☆☆', color: 'text-red-600', sentiment: '👎 Disliked' },
    { value: 2, label: 'Poor', icon: '⭐⭐☆☆☆', color: 'text-red-500', sentiment: '👎 Disliked' },
    { value: 3, label: 'Neutral', icon: '⭐⭐⭐☆☆', color: 'text-yellow-500', sentiment: '😐 Neutral' },
    { value: 4, label: 'Good', icon: '⭐⭐⭐⭐☆', color: 'text-green-500', sentiment: '👍 Liked' },
    { value: 5, label: 'Excellent', icon: '⭐⭐⭐⭐⭐', color: 'text-green-600', sentiment: '👍 Liked' }
];

// Focusing on data object specific feedback

const SEVERITY_LEVELS = [
    { value: 'low', label: 'Low - Minor inconvenience' },
    { value: 'medium', label: 'Medium - Affects workflow' },
    { value: 'high', label: 'High - Blocks functionality' },
    { value: 'critical', label: 'Critical - System unusable' }
];

const PROBLEM_CATEGORIES = [
    'Data Quality',
    'Performance',
    'Analysis Error',
    'Upload Issue',
    'Processing Failure',
    'Visualization',
    'Export Problem',
    'Missing Data',
    'Incorrect Results',
    'System Error'
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({
    isOpen,
    onClose,
    dataSources = [],
    historicalReports = [],
    currentReportId,
    currentReportName
}) => {
    const { push: pushToast } = useToast();
    const { user } = useAuth();
    const { t } = useTranslate();

    const [formData, setFormData] = useState<FeedbackFormData>({
        rating: 0,
        likeDislike: null,
        entityType: 'data_object',
        dataObjectId: null,
        dataObjectName: null,
        selectedReport: null,
        severity: 'medium',
        tags: [],
        feedback: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Create set of current data object UUIDs for filtering (same logic as history tab)
    const currentDataObjectIds = useMemo(() => {
        return new Set(dataSources.map(obj => obj.uuid).filter(Boolean));
    }, [dataSources]);

    // Filter reports to only show those for current workspace data objects (same as history tab)
    const workspaceFilteredReports = useMemo(() => {
        if (!historicalReports || historicalReports.length === 0) {
            return [];
        }
        if (currentDataObjectIds.size === 0) return historicalReports;
        return historicalReports.filter(report =>
            report.data_object_id && currentDataObjectIds.has(report.data_object_id)
        );
    }, [historicalReports, currentDataObjectIds]);

    // Use workspace-filtered reports - show only reports for current data objects
    const availableReports = useMemo(() => {

        if (workspaceFilteredReports && workspaceFilteredReports.length > 0) {
            // Group reports by data object to avoid duplicates - keep most recent per data object
            const reportMaps = new Map<string, HistoricalReportResponse>();

            // Process only workspace-filtered reports
            workspaceFilteredReports.forEach(report => {
                if (report.data_object_name && report.data_object_id) {
                    const key = report.data_object_id;
                    const existing = reportMaps.get(key);

                    // Keep the most recent report (highest ID as proxy for most recent)
                    if (!existing || report.id > existing.id) {
                        reportMaps.set(key, report);
                    }
                }
            });

            // Convert back to array and sort by data object name
            const uniqueReports = Array.from(reportMaps.values())
                .sort((a, b) => (a.data_object_name || '').localeCompare(b.data_object_name || ''));

            return uniqueReports.map((report, index) => ({
                id: report.id || `historical_${index}`, // This is the actual report ID
                reportId: report.id, // Store the actual report ID separately
                name: report.data_object_name || `Report ${index + 1}`,
                dataObjectId: report.data_object_id,
                timestamp: report.generated_at || new Date().toISOString().slice(0, 19).replace('T', ' '),
                reportType: 'historical'
            }));
        }

        // Fallback: use current data sources (won't show duplicates from history)
        return dataSources
            .filter(ds => ds.status === 'processed' && ds.uuid)
            .map(ds => ({
                id: ds.uuid, // Use data object UUID for display
                reportId: null, // No actual report ID for current data sources
                name: ds.name,
                dataObjectId: ds.uuid,
                timestamp: ds.createdAt || new Date().toISOString().slice(0, 19).replace('T', ' '),
                reportType: 'current'
            }));
    }, [dataSources, historicalReports, workspaceFilteredReports]);

    const handleRatingChange = (rating: number) => {
        // Auto-derive like/dislike from star rating
        let likeDislike: boolean | null = null;
        if (rating >= 1 && rating <= 2) {
            likeDislike = false; // Bad experience (1-2 stars)
        } else if (rating === 3) {
            likeDislike = null; // Neutral experience (3 stars)  
        } else if (rating >= 4 && rating <= 5) {
            likeDislike = true; // Good experience (4-5 stars)
        }

        setFormData(prev => ({
            ...prev,
            rating,
            likeDislike // Auto-set based on rating
        }));
        setErrors(prev => ({ ...prev, rating: '', likeDislike: '' }));
    };

    const handleLikeDislikeChange = (value: boolean) => {
        setFormData(prev => ({
            ...prev,
            likeDislike: prev.likeDislike === value ? null : value
        }));
    };

    // Handle report selection directly
    const handleReportSelection = (reportId: string) => {
        const selectedReport = reportId
            ? availableReports.find(report => report.id === reportId)
            : null;
        setFormData(prev => ({
            ...prev,
            selectedReport: selectedReport ? {
                id: selectedReport.id,
                reportId: selectedReport.reportId, // Actual report ID (null for current data sources)
                name: selectedReport.name,
                timestamp: selectedReport.timestamp,
                dataObjectId: selectedReport.dataObjectId,
                reportType: selectedReport.reportType as 'current' | 'historical'
            } : null
        }));
    };

    // Entity type is fixed to data_object for focused feedback

    const handleDataObjectChange = (dataObjectId: string) => {
        const selectedDataObject = dataSources.find(ds => ds.uuid === dataObjectId);
        setFormData(prev => ({
            ...prev,
            dataObjectId: selectedDataObject ? selectedDataObject.uuid : null,
            dataObjectName: selectedDataObject ? selectedDataObject.name : null
        }));
    };

    const handleTagToggle = (tag: string) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.includes(tag)
                ? prev.tags.filter(t => t !== tag)
                : [...prev.tags, tag]
        }));
    };

    const handleCustomTagAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const customTag = e.currentTarget.value.trim();
            if (customTag && !formData.tags.includes(customTag)) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...prev.tags, customTag]
                }));
                e.currentTarget.value = '';
            }
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.rating || formData.rating < 1) {
            newErrors.rating = t('Please select a rating (1-5 stars)');
        }

        if (!formData.feedback?.trim()) {
            newErrors.feedback = t('Please provide your feedback');
        }

        // No report selection validation - always optional

        setErrors(newErrors);

        // Show toast message for validation failures
        if (Object.keys(newErrors).length > 0) {
            if (newErrors.rating && newErrors.feedback) {
                pushToast({
                    variant: 'warning',
                    message: t('Please provide both a rating and your feedback comment')
                });
            } else if (newErrors.rating) {
                pushToast({
                    variant: 'warning',
                    message: t('Please select a rating (1-5 stars)')
                });
            } else if (newErrors.feedback) {
                pushToast({
                    variant: 'warning',
                    message: t('Please enter your feedback comment as well')
                });
            }
        }

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!user?.email) {
            pushToast({ variant: 'error', message: t('User not authenticated. Please log in to submit feedback.') });
            return;
        }

        setIsSubmitting(true);

        try {
            // Determine which report to use: current report from page takes precedence
            const reportToSubmit = (currentReportId && currentReportName) ? {
                id: currentReportId,
                reportId: currentReportId,
                name: currentReportName,
                reportType: 'historical' as const
            } : formData.selectedReport;

            const result = await feedbackService.submitFeedback(
                {
                    ...formData,
                    selectedReport: reportToSubmit
                },
                user.email
            );

            pushToast({
                variant: 'success',
                message: t('Thank you for your feedback! We appreciate your input.')
            });

            // Reset form
            setFormData({
                rating: 0,
                likeDislike: null,
                entityType: 'data_object',
                dataObjectId: null,
                dataObjectName: null,
                selectedReport: null,
                severity: 'medium',
                tags: [],
                feedback: ''
            });

            onClose();
        } catch (error) {
            console.error('❌ FEEDBACK SUBMISSION ERROR:', error);
            pushToast({
                variant: 'error',
                message: t('Failed to submit feedback. Please try again.')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            rating: 0,
            likeDislike: null,
            entityType: 'data_object',
            dataObjectId: null,
            dataObjectName: null,
            selectedReport: null,
            severity: 'medium',
            tags: [],
            feedback: ''
        });
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-1 sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-modal-title"
        >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            <div
                className="relative rounded-2xl shadow-2xl bg-white transform transition-all duration-300 flex flex-col max-h-[calc(100vh-0.5rem)] w-full max-w-[calc(100vw-0.5rem)] sm:max-w-lg md:max-w-2xl lg:max-w-3xl"
                style={{
                    boxShadow: '0 20px 40px -10px rgba(220, 38, 38, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
            >
                {/* Compact Header */}
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-2 sm:px-4 md:px-6 py-3 sm:py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                <VscFeedback className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <div>
                                <h2 id="feedback-modal-title" className="text-lg sm:text-xl font-bold text-white">
                                    {t('Share Your Feedback')}
                                </h2>
                                <p className="text-red-100 text-xs sm:text-sm">
                                    {t('Help us improve your experience')}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                            <VscClose className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                </div>

                {/* Compact Form Content */}
                <div className="flex-1 overflow-y-auto p-2 sm:p-4 md:p-6">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Single Column Layout for Better Space Usage */}

                        {/* Star Rating Section */}
                        <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-xl border border-red-100">
                            <label className="block text-lg font-bold text-gray-900 mb-3 text-center">
                                {t('Rate your experience')} *
                            </label>
                            {errors.rating && (
                                <p className="text-red-600 text-sm mb-3 text-center bg-red-50 p-2 rounded-lg">{errors.rating}</p>
                            )}

                            {/* Compact Star Rating */}
                            <div className="star-rating-container">
                                {[5, 4, 3, 2, 1].map((rating) => (
                                    <React.Fragment key={rating}>
                                        <input
                                            type="radio"
                                            id={`star${rating}`}
                                            name="rating"
                                            value={rating}
                                            checked={formData.rating === rating}
                                            onChange={() => handleRatingChange(rating)}
                                        />
                                        <label htmlFor={`star${rating}`}></label>
                                    </React.Fragment>
                                ))}
                            </div>

                            {/* Compact Rating Feedback */}
                            {formData.rating > 0 && (
                                <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                                    <p className="text-blue-700 text-sm text-center font-medium">
                                        {formData.rating >= 4 ? t('Thank you for the positive feedback!') :
                                            formData.rating === 3 ? t('Thanks for your feedback') :
                                                t("We'll work to improve your experience")}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Report Selection - Always Optional or Display Current Report */}
                        {currentReportId && currentReportName ? (
                            <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                                <label className="block text-sm font-bold text-gray-900 mb-2">
                                    {t('Report')}
                                </label>
                                <div className="w-full px-3 py-2 bg-white border border-red-300 rounded-lg text-gray-700 text-sm">
                                    {currentReportName}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                                <label className="block text-sm font-bold text-gray-900 mb-2">
                                    {t('Select Report (Optional)')}
                                </label>
                                {availableReports.length === 0 ? (
                                    <div className="px-3 py-2 bg-yellow-50 text-gray-600 text-xs rounded-lg border">
                                        {t('No reports available')}
                                    </div>
                                ) : (
                                    <select
                                        value={formData.selectedReport?.id || ''}
                                        onChange={(e) => handleReportSelection(e.target.value)}
                                        className="w-full px-3 py-2 border border-red-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                                    >
                                        <option value="">{t('Select report...')}</option>
                                        {availableReports.map((report) => {
                                            // Format timestamp for better readability
                                            const formatTimestamp = (timestamp: string): string => {
                                                try {
                                                    const date = new Date(timestamp);
                                                    const options: Intl.DateTimeFormatOptions = {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: true
                                                    };
                                                    return date.toLocaleDateString('en-US', options);
                                                } catch {
                                                    return timestamp.slice(0, 16).replace('T', ' ');
                                                }
                                            };

                                            // Create descriptive label with name and timestamp only
                                            const displayLabel = `${report.name} (${formatTimestamp(report.timestamp)})`;

                                            return (
                                                <option key={report.id} value={report.id} title={`Created: ${report.timestamp}`}>
                                                    {displayLabel}
                                                </option>
                                            );
                                        })}
                                    </select>
                                )}
                            </div>
                        )}

                        {/* Feedback Description */}
                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                            <label className="block text-sm font-bold text-gray-900 mb-2">
                                {t('Your feedback')} *
                            </label>
                            {errors.feedback && (
                                <p className="text-red-600 text-sm mb-2 p-2 bg-red-50 rounded-lg">{errors.feedback}</p>
                            )}
                            <textarea
                                value={formData.feedback}
                                onChange={(e) => setFormData(prev => ({ ...prev, feedback: e.target.value }))}
                                placeholder={t("We'd love to hear your full experience\u2014tell us what went right and what could be better.")}
                                rows={4}
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500 resize-none text-sm"
                            />
                            <div className="flex justify-between items-center mt-2">
                                <p className="text-blue-600 text-xs">{t('Your feedback helps us improve')}</p>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Fixed Footer with Action Buttons */}
                <div className="flex-shrink-0 px-2 sm:px-4 md:px-6 py-3 sm:py-4 border-t bg-gray-50 rounded-b-2xl">
                    <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="w-full sm:w-auto px-3 sm:px-6 py-2 text-sm sm:text-base text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50 order-2 sm:order-1"
                            disabled={isSubmitting}
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            onClick={handleSubmit}
                            className="w-full sm:w-auto px-3 sm:px-6 py-2 text-sm sm:text-base bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-medium transition-colors disabled:opacity-50 shadow-lg order-1 sm:order-2"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t('Submitting...')}
                                </span>
                            ) : t('Submit Feedback')}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default FeedbackModal;